const refreshFailedPhase = "refreshFailed";
const requestTimeoutStatus = 408;
const serverErrorStatus = 500;
// Both Shared and product HTTP adapters expose numeric status on their Error instances.
const uncertainTransportFailure = error => error instanceof TypeError || error instanceof SyntaxError || error instanceof Error
    && typeof error.status === "number" && (error.status === 0 || error.status === requestTimeoutStatus || error.status >= serverErrorStatus);

/** Resource-scoped lifecycle independent of the editor's mounting and of any product or transport. */
export function createOperationStore() {
    let active = true;
    let snapshot = [];
    const listeners = new Set();
    const refreshers = new Map();
    const reconciliations = new Map();
    const runningRetries = new Map();
    const generations = new Map();
    const readers = new Set();
    const publish = (entry, remove = false) => {
        if (!active)
            return;
        snapshot = [...snapshot.filter(current => current.id !== entry.id), ...(remove ? [] : [entry])];
        listeners.forEach(listener => listener());
        readers.forEach(check => check());
    };
    const retryRead = async (id) => {
        const entry = snapshot.find(current => current.id === id);
        const refresh = refreshers.get(id);
        if (!active || !entry || entry.phase !== refreshFailedPhase || !refresh)
            return;
        try {
            if (entry.writeUncertain) {
                const reconcile = reconciliations.get(id);
                if (!reconcile || !await reconcile() || !active)
                    return;
            }
            publish({ ...entry, phase: "refreshing", error: undefined });
            await refresh();
            if (!active)
                return;
            refreshers.delete(id);
            reconciliations.delete(id);
            publish(entry, true);
        }
        catch (error) {
            if (active)
                publish({ ...entry, phase: refreshFailedPhase, error });
        }
    };
    const retry = (id) => {
        const running = runningRetries.get(id);
        if (running)
            return running;
        const run = Promise.resolve().then(() => retryRead(id)).finally(() => runningRetries.delete(id));
        runningRetries.set(id, run);
        return run;
    };
    const conflicts = (resources) => snapshot.some(entry => entry.resources.some(resource => resources.includes(resource)));
    const affectsRead = (entry, resources) => [...entry.resources, ...(entry.invalidates ?? [])].some(resource => resources.includes(resource));
    const waitForIdle = resources => new Promise((resolve, reject) => {
        const check = () => {
            if (!active) { readers.delete(check); reject(new Error("Operation scope expired")); return; }
            const entries = snapshot.filter(entry => affectsRead(entry, resources));
            const failed = entries.find(entry => entry.phase === refreshFailedPhase);
            if (failed) { readers.delete(check); reject(failed.error); return; }
            if (!entries.length) { readers.delete(check); resolve(); }
        };
        readers.add(check); check();
    });
    return {
        dispose() { active = false; snapshot = []; refreshers.clear(); reconciliations.clear(); runningRetries.clear(); listeners.clear(); generations.clear(); readers.forEach(check => check()); readers.clear(); },
        subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        getSnapshot: () => snapshot,
        isActive: () => active,
        retry,
        conflicts,
        /** Initial/detail reads only. A mutation's refresh reads directly to avoid waiting for itself. */
        async read(resources, load) {
            if (!resources.length) throw new Error("Operation resources required");
            while (active) {
                await waitForIdle(resources);
                const generation = resources.map(resource => generations.get(resource));
                const value = await load();
                if (!active) throw new Error("Operation scope expired");
                if (!snapshot.some(entry => affectsRead(entry, resources)) && resources.every((resource, index) => generations.get(resource) === generation[index])) return value;
            }
            throw new Error("Operation scope expired");
        },
        async execute({ resources, invalidates = [], operation, refresh, isUncertainWriteError, reconcileWrite, refreshAfterRejectedWrite = false }) {
            if (!active)
                return "cancelled";
            if (resources.length === 0)
                throw new Error("Operation resources required");
            if (conflicts(resources))
                return "conflict";
            new Set([...resources, ...invalidates]).forEach(resource => generations.set(resource, (generations.get(resource) ?? 0) + 1));
            let entry = { id: Symbol(), resources: [...new Set(resources)], invalidates: [...new Set(invalidates)], phase: "writing" };
            let written = false;
            publish(entry);
            try {
                await operation(() => {
                    if (!active)
                        throw new Error("Operation scope expired");
                    if (written)
                        return;
                    written = true;
                    entry = { ...entry, phase: "refreshing" };
                    publish(entry);
                });
                if (!active)
                    return "cancelled";
                if (!written) {
                    publish(entry, true);
                    return "cancelled";
                }
                await refresh();
                if (!active)
                    return "cancelled";
                publish(entry, true);
                return "completed";
            }
            catch (error) {
                if (!active)
                    return "cancelled";
                if (!written && !uncertainTransportFailure(error) && !isUncertainWriteError?.(error)) {
                    if (refreshAfterRejectedWrite) {
                        entry = { ...entry, phase: "refreshing", writeRejected: true };
                        publish(entry);
                        try {
                            await refresh();
                            if (!active) return "cancelled";
                        } catch (readError) {
                            if (!active) return "cancelled";
                            refreshers.set(entry.id, refresh);
                            publish({ ...entry, phase: refreshFailedPhase, retryable: true, error: readError });
                            return "refreshFailed";
                        }
                    }
                    publish(entry, true);
                    throw error;
                }
                // Retry reads only. In particular, never repeat post-write callbacks (mail, reminders, transfers).
                refreshers.set(entry.id, refresh);
                if (!written && reconcileWrite)
                    reconciliations.set(entry.id, reconcileWrite);
                publish({ ...entry, phase: refreshFailedPhase, writeUncertain: !written, retryable: written || Boolean(reconcileWrite), error });
                return written ? "refreshFailed" : "writeUncertain";
            }
        },
    };
}
